import React, { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import TabManager from "../../components/Layout/TabManager/TabManager";
import FilteredTableCore from "../../components/Organismos/Table/FilteredTableCore";
import PaginatedTable from "../../components/Organismos/Table/PaginatedTable";
import useDataLoader2 from "../../hooks/useDataLoader2";
import { defaultPageSize } from "../../utils/const";
import { formatTimeSpanToString } from "../../utils/functions";
import ShapeEdit from "../../components/Atomos/Icons/ShapeEdit";
import Button from "../../components/staff/Button";
import {
  getAuditPersistentFilter,
  getModifiedLastWeekPersistentFilter,
  PERSON_AUDIT_ENDPOINT,
  type PersonAuditFilterKey,
  type PersonAuditRow,
} from "./personAuditFilters";
import {
  runPersonAudit,
  type PersonAuditScanType,
} from "../../Services/Person";
import type { IHeader } from "../../components/Organismos/Table/FilteredTable.types";
import type { IPagination2, ISorting } from "../../interfaces/Components/ITable";

const TAB_LABELS = [
  "Sin userId",
  "B2C huérfanos",
  "Sin legacyId",
  "Email duplicado",
  "Persona duplicada",
  "Modificadas (7 días)",
  "Sin AOP",
  "Sin role/username",
] as const;

const editColumn: IHeader = {
  id: "edit",
  header: "Edit",
  enableSorting: false,
  width: 70,
  cell: ({ row }: { row: PersonAuditRow }) => (
    <Link
      className="flex items-center text-gray-font"
      to={`/catalogs/person/edit/${row.id}`}
    >
      <span className="w-4 h-4 inline-block">
        <ShapeEdit />
      </span>
    </Link>
  ),
};

function buildBaseColumns(options?: {
  showDuplicate?: boolean;
  showModified?: boolean;
}): IHeader[] {
  const cols: IHeader[] = [
    { id: "generatedId", header: "Generated ID", width: 110 },
    { id: "name", header: "Name", width: 120 },
    { id: "lastName", header: "Last Name", width: 120 },
    { id: "firmName", header: "Firm", width: 160 },
    { id: "firmEmail", header: "Firm Email", width: 180 },
    { id: "userId", header: "User ID", width: 200 },
    { id: "legacyId", header: "Legacy ID", width: 100 },
    { id: "role", header: "Role", width: 100 },
    { id: "userName", header: "Username", width: 120 },
  ];

  if (options?.showDuplicate) {
    cols.push(
      { id: "duplicateGroup", header: "Grupo", width: 200 },
      { id: "groupCount", header: "En grupo", width: 90 },
    );
  }

  if (options?.showModified) {
    cols.push({
      id: "_ts",
      header: "Modificado",
      width: 110,
      cell: ({ value }: { value: unknown }) =>
        formatTimeSpanToString(value as string | number),
    });
  }

  cols.push(editColumn);
  return cols;
}

function AuditSection({
  description,
  totalCount,
  headerExtra,
  children,
}: {
  description: string;
  totalCount?: number;
  headerExtra?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="mt-4">
      <AuditSectionHeader
        description={description}
        totalCount={totalCount}
        headerExtra={headerExtra}
      />
      {children}
    </div>
  );
}

const AuditPaginatedTab: React.FC<{
  auditFilter: PersonAuditFilterKey;
  description: string;
}> = ({ auditFilter, description }) => {
  const endpoint = `${PERSON_AUDIT_ENDPOINT}?auditFilter=${auditFilter}`;
  const persistentFilter = useMemo(
    () => getAuditPersistentFilter(auditFilter),
    [auditFilter],
  );
  const columnsDef = useMemo(() => buildBaseColumns(), []);

  const { data, totalCount, sort, setSort, pagination, setPagination } =
    useDataLoader2<PersonAuditRow>({
      endpoint,
      persistentFilter,
      defaultPageSize,
      defaultSorting: { field: "name", order: "asc" },
      tableKey: `/catalogs/person-audit/${auditFilter}`,
    });

  return (
    <AuditSection description={description} totalCount={totalCount}>
      <PaginatedTable
        data={data ?? []}
        columnsDef={columnsDef}
        sorting={sort}
        onSortingChange={setSort}
        totalCount={totalCount ?? 0}
        pagination={pagination}
        onPaginationChange={setPagination}
        endpoint={endpoint}
      />
    </AuditSection>
  );
};

const B2C_ORPHAN_BATCH_SIZE = 500;
const B2C_SCAN_MAX_BATCHES = 2000;

const B2cOrphansScanTab: React.FC<{ description: string }> = ({ description }) => {
  const [items, setItems] = useState<PersonAuditRow[]>([]);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scanned, setScanned] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);

  const columnsDef = useMemo(() => buildBaseColumns(), []);
  const [pagination, setPagination] = useState<IPagination2>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [sort, setSort] = useState<ISorting>({ field: "name", order: "asc" });

  const runScan = useCallback(async () => {
    setLoading(true);
    setError(null);
    setProgress(null);
    const accumulated: PersonAuditRow[] = [];
    let batchNum = 0;
    let hasMore = true;
    let totalToScan: number | undefined;
    let lastId: string | undefined;
    let scannedCount = 0;

    try {
      setProgress("Descargando índice de usuarios Azure B2C…");
      const b2cIndex = await runPersonAudit("b2cAllUserIds");
      const b2cUserIds = b2cIndex.userIds ?? [];
      const b2cSet = new Set(
        b2cUserIds.map((id) => String(id).trim()).filter(Boolean),
      );

      setProgress(
        `${b2cSet.size.toLocaleString()} usuarios en B2C. Escaneando Cosmos…`,
      );

      while (hasMore && batchNum < B2C_SCAN_MAX_BATCHES) {
        setProgress(
          totalToScan != null
            ? `Lote ${batchNum + 1}: ${Math.min(scannedCount, totalToScan)} / ${totalToScan} personas con userId…`
            : `Lote ${batchNum + 1}: consultando Cosmos…`,
        );

        const batch = await runPersonAudit("b2cOrphans", {
          pageSize: B2C_ORPHAN_BATCH_SIZE,
          lastId,
        });

        if (batch.personsWithUserIdTotal != null) {
          totalToScan = batch.personsWithUserIdTotal;
        }

        const orphansInBatch = batch.items.filter((p) => {
          const uid = String(p.userId ?? "").trim();
          return uid && !b2cSet.has(uid);
        });
        accumulated.push(...orphansInBatch);
        scannedCount += batch.scannedInBatch ?? 0;
        lastId = batch.nextLastId;
        hasMore = batch.hasMore === true;
        batchNum += 1;

        setProgress(
          totalToScan != null
            ? `${accumulated.length} huérfano(s) · ${Math.min(scannedCount, totalToScan)} / ${totalToScan} revisadas`
            : `${accumulated.length} huérfano(s) · lote ${batchNum} completado`,
        );
      }

      setItems(accumulated);
      setTotalCount(accumulated.length);
      setScanned(true);
      setPagination((p) => ({ ...p, pageIndex: 0 }));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error al ejecutar el escaneo");
      setItems(accumulated);
      setTotalCount(accumulated.length);
      setScanned(true);
    } finally {
      setLoading(false);
      setProgress(null);
    }
  }, []);

  return (
    <AuditSection
      description={description}
      totalCount={totalCount ?? undefined}
      headerExtra={
        <Button
          text="Escanear B2C"
          color="secondary"
          variant="filled"
          size="fixed"
          onClick={() => void runScan()}
          disabled={loading}
        />
      }
    >
      {loading && progress && (
        <p className="text-sm text-gray-font mb-2">{progress}</p>
      )}
      {loading && !progress && (
        <p className="text-sm text-gray-font mb-2">Iniciando escaneo…</p>
      )}
      {error && <p className="text-sm text-red-600 mb-2">{error}</p>}
      {scanned && !loading && (
        <PaginatedTable
          data={items}
          columnsDef={columnsDef}
          sorting={sort}
          onSortingChange={setSort}
          totalCount={items.length}
          pagination={pagination}
          onPaginationChange={setPagination}
          endpoint=""
        />
      )}
    </AuditSection>
  );
};

const AuditScanTab: React.FC<{
  audit: PersonAuditScanType;
  description: string;
  buttonLabel: string;
  showDuplicate?: boolean;
}> = ({ audit, description, buttonLabel, showDuplicate }) => {
  const [items, setItems] = useState<PersonAuditRow[]>([]);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scanned, setScanned] = useState(false);

  const columnsDef = useMemo(
    () => buildBaseColumns({ showDuplicate }),
    [showDuplicate],
  );

  const [pagination, setPagination] = useState<IPagination2>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [sort, setSort] = useState<ISorting>({
    field: "duplicateGroup",
    order: "asc",
  });

  const runScan = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await runPersonAudit(audit);
      setItems(result.items);
      setTotalCount(result.totalCount);
      setScanned(true);
      setPagination((p) => ({ ...p, pageIndex: 0 }));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error al ejecutar el escaneo");
      setItems([]);
      setTotalCount(0);
      setScanned(true);
    } finally {
      setLoading(false);
    }
  }, [audit]);

  return (
    <AuditSection
      description={description}
      totalCount={totalCount ?? undefined}
      headerExtra={
        <Button
          text={buttonLabel}
          color="secondary"
          variant="filled"
          size="fixed"
          onClick={() => void runScan()}
          disabled={loading}
        />
      }
    >
      {loading && (
        <p className="text-sm text-gray-font mb-2">
          Escaneando… puede tardar unos minutos.
        </p>
      )}
      {error && <p className="text-sm text-red-600 mb-2">{error}</p>}
      {scanned && !loading && (
        <PaginatedTable
          data={items}
          columnsDef={columnsDef}
          sorting={sort}
          onSortingChange={setSort}
          totalCount={items.length}
          pagination={pagination}
          onPaginationChange={setPagination}
          endpoint=""
        />
      )}
    </AuditSection>
  );
};

function AuditSectionHeader({
  description,
  totalCount,
  headerExtra,
}: {
  description: string;
  totalCount?: number;
  headerExtra?: React.ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
      <p className="text-sm text-gray-font">{description}</p>
      <div className="flex items-center gap-3">
        {totalCount !== undefined && (
          <span className="text-sm font-medium">
            Total: {totalCount.toLocaleString()}
          </span>
        )}
        {headerExtra}
      </div>
    </div>
  );
}

const ModifiedLastWeekTab: React.FC = () => {
  const persistentFilter = useMemo(
    () => getModifiedLastWeekPersistentFilter(),
    [],
  );
  const columnsDef = useMemo(() => buildBaseColumns({ showModified: true }), []);

  return (
    <AuditSection description="Personas con _ts en los últimos 7 días (rolling).">
      <FilteredTableCore
        columnsDef={columnsDef}
        endpoint="/person"
        filtersData={[]}
        persistentFilter={persistentFilter}
        defaultSorting={{ field: "_ts", order: "desc" }}
        defaultPageSize={20}
        persistFilters={false}
        showExcelExport
        excelFilename="person-modified-last-7-days.xlsx"
      />
    </AuditSection>
  );
};

const PersonDataAudit: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState(0);

  const tabs = useMemo(
    () =>
      TAB_LABELS.map((title, index) => ({
        title,
        action: () => setSelectedTab(index),
      })),
    [],
  );

  return (
    <div className="">
      <p className="text-sm text-gray-font mb-4">
        Herramienta temporal de diagnóstico (solo WSG Super Admin).
      </p>

      <TabManager tabs={tabs} selectedTabIndex={selectedTab} />

      <div className="bg-white border border-gray-200 p-4 min-h-[24rem]">
        {selectedTab === 0 && (
          <AuditPaginatedTab
            auditFilter="noUserId"
            description="Personas sin userId en Cosmos."
          />
        )}
        {selectedTab === 1 && (
          <B2cOrphansScanTab description="Personas con userId en Cosmos que no existen en Azure B2C. Primero descarga todos los ids de B2C; luego escanea Cosmos por lotes." />
        )}
        {selectedTab === 2 && (
          <AuditPaginatedTab
            auditFilter="noLegacyId"
            description="Personas sin legacyId."
          />
        )}
        {selectedTab === 3 && (
          <AuditScanTab
            audit="duplicateFirmEmail"
            description="Mismo firmEmail (normalizado, sin distinguir mayúsculas)."
            buttonLabel="Buscar emails duplicados"
            showDuplicate
          />
        )}
        {selectedTab === 4 && (
          <AuditScanTab
            audit="duplicateIdentity"
            description="Mismo firm + name + lastName (normalizado)."
            buttonLabel="Buscar personas duplicadas"
            showDuplicate
          />
        )}
        {selectedTab === 5 && <ModifiedLastWeekTab />}
        {selectedTab === 6 && (
          <AuditPaginatedTab
            auditFilter="noAop"
            description="Sin areasOfPractice ni aopExpertiseIds asignados."
          />
        )}
        {selectedTab === 7 && (
          <AuditPaginatedTab
            auditFilter="noRoleOrUsername"
            description="Sin role o sin userName."
          />
        )}
      </div>
    </div>
  );
};

export default PersonDataAudit;
